import {useState} from "react";
import {CreateFormHandler} from "./Form";
import {Page} from "../pages";
import FormattedButton from "../FormattedButton";
import {useAddressBookCRUD} from "./tools";
import {AddressBookCRUD} from "./AddressBookCRUD";
import Loading from "@/components/breadcrumbs/Loading";
import {useDomainNameQuery} from "@/queries.generated";
import {useBoolState} from "@/hooks";
import {Dialog, TextField, Typography} from "@mui/material";
import {defineMessage, FormattedMessage, useIntl} from "react-intl";
import {TailSpin, DialogActions, DialogContent, FormattedDialogTitle} from "@dvrt/graphql-widgets";
import {
    useSynchronizeAddressBookMutation,
    SynchronizeAddressBookMutation,
    useAddAddressBookEntryMutation,
    useUpdateAddressBookEntryMutation,
} from "./queries.generated";
import {commonTranslations} from "@/utils/translations";

const divStyle = {
    display: "flex",
    alignItems: "baseline",
};

const inputStyle = {
    flexGrow: 1,
};

const buttonStyle = {
    marginLeft: 10,
};

export const DomainAddressBookPage = ({domainId}: {domainId: number}) => {
    const {data} = useDomainNameQuery({
        variables: {id: domainId},
        fetchPolicy: "no-cache",
    });

    if (!data) return <Loading />;

    return <Wrapper domainName={data.domains.items[0].name} domainId={domainId} />;
};

const Synchronize = ({domainId, onSyncDone}: {domainId: number; onSyncDone: () => void}) => {
    const [isShowDialog, showDialog, hideDialog] = useBoolState();

    const [synchronizeAddressBook] = useSynchronizeAddressBookMutation({variables: {domainId}});

    const [result, setResult] = useState<SynchronizeAddressBookMutation["synchronizeDomainAddressBook"]>();

    const startSync = async (): Promise<void> => {
        showDialog();
        setResult(null);

        const res = await synchronizeAddressBook();

        setResult(res.data.synchronizeDomainAddressBook);

        if (
            res.data.synchronizeDomainAddressBook.createdEntries ||
            res.data.synchronizeDomainAddressBook.updatedEntries
        )
            onSyncDone();
    };

    return (
        <>
            <FormattedButton
                onClick={startSync}
                message={defineMessage({id: "address_book.sync.button", defaultMessage: "Synchronize"})}
            />
            <Dialog open={isShowDialog} onClose={hideDialog}>
                <FormattedDialogTitle
                    title={defineMessage({
                        id: "address_book.sync.title",
                        defaultMessage: "Synchronize domain address book",
                    })}
                />
                <DialogContent>
                    {!result && (
                        <div style={{margin: 10, textAlign: "center"}}>
                            <TailSpin height={40} />
                        </div>
                    )}
                    {result && (
                        <>
                            <Typography>
                                <FormattedMessage id="address_book.sync.created" defaultMessage="Created entries" />:{" "}
                                {result?.createdEntries}
                            </Typography>
                            <Typography>
                                <FormattedMessage id="address_book.sync.updated" defaultMessage="Updated entries" />:{" "}
                                {result?.updatedEntries}
                            </Typography>
                            <Typography>
                                <FormattedMessage id="address_book.sync.deleted" defaultMessage="Deleted entries" />:{" "}
                                {result?.deletedEntries}
                            </Typography>
                            <Typography>
                                <FormattedMessage id="address_book.sync.failed" defaultMessage="Failed entries" />:{" "}
                                {result?.failedEntries}
                            </Typography>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <FormattedButton
                        message={defineMessage({id: "close", defaultMessage: "Close"})}
                        variant="text"
                        onClick={hideDialog}
                    />
                </DialogActions>
            </Dialog>
        </>
    );
};

const Filter = ({
    refetch,
    domainName,
    value,
}: {
    refetch: (params: {target: string; filterValue: string}) => void;
    domainName: string;
    value: {target: {value: string}};
}) => {
    refetch({target: domainName, filterValue: value.target.value});
};

const Wrapper = ({domainName, domainId}: {domainName: string; domainId: number}) => {
    const {data, deleteEntryName, formHandler, refetch, setDeleteEntryName, setFormHandler} =
        useAddressBookCRUD(domainName);
    const intl = useIntl();

    const [createMutation] = useAddAddressBookEntryMutation();
    const [updateMutation] = useUpdateAddressBookEntryMutation();

    const defaultFormHandler = new CreateFormHandler(createMutation);

    const controls = (
        <div style={divStyle}>
            <TextField
                variant="standard"
                style={inputStyle}
                placeholder={intl.formatMessage(commonTranslations.filterPlaceholders.nameOrPhoneNumber)}
                onChange={(event) => {
                    Filter({refetch, domainName, value: event});
                }}
            ></TextField>
            <div style={buttonStyle}>
                <Synchronize domainId={domainId} onSyncDone={refetch} />
                <FormattedButton
                    onClick={() => setFormHandler(defaultFormHandler)}
                    message={defineMessage({id: "address_book.create", defaultMessage: "Create new entry"})}
                    style={{marginLeft: "1em"}}
                />
            </div>
        </div>
    );

    return (
        <Page title={defineMessage({id: "address_book.title", defaultMessage: "Address book"})} controls={controls}>
            <AddressBookCRUD
                target={domainName}
                data={data}
                deleteEntryName={deleteEntryName}
                formHandler={formHandler}
                refetch={refetch}
                setDeleteEntryName={setDeleteEntryName}
                setFormHandler={setFormHandler}
                updateMutation={updateMutation}
            />
        </Page>
    );
};
